"use client";

/**
 * Font pairing registry for carousel templates.
 *
 * next/font/google loaders must be module-scope constants — families cannot
 * be loaded dynamically, so every pairing offered anywhere in the studio is
 * declared here. Each family adds CSS + lazy woff2 weight; keep the total
 * at ~5 families max (all with latin+cyrillic so any language renders).
 *
 * next/font generates obfuscated font-family names at build time — there is
 * no literal "Cormorant Garamond" available to Canvas. The registry exposes
 * the resolved names (e.g. `cormorant.style.fontFamily`).
 */

import { Cormorant_Garamond, Montserrat, Playfair_Display, Inter, Unbounded, Manrope, Caveat } from "next/font/google";

const cormorant = Cormorant_Garamond({
  subsets: ["latin", "cyrillic"],
  weight: ["300", "400", "500", "600"],
  style: ["normal", "italic"],
});
const montserrat = Montserrat({
  subsets: ["latin", "cyrillic"],
  weight: ["300", "400", "500", "600"],
});
const playfair = Playfair_Display({
  subsets: ["latin", "cyrillic"],
  weight: ["400", "500", "600"],
  style: ["normal", "italic"],
});
const inter = Inter({
  subsets: ["latin", "cyrillic"],
  weight: ["300", "400", "500", "600"],
});
// "Modern" pairing: Unbounded/Manrope chosen over the Fraunces/Work Sans
// idea because both keep cyrillic coverage (Fraunces is latin-only).
const unbounded = Unbounded({
  subsets: ["latin", "cyrillic"],
  weight: ["300", "400", "600"],
});
const manrope = Manrope({
  subsets: ["latin", "cyrillic"],
  weight: ["300", "400", "500", "600"],
});
// Handwritten accent family used by the "Handwritten" designer cover
// (script kicker / stat labels / "смотри дальше" CTA). Not part of any
// pairing — templates that need it import SCRIPT_FONT directly.
const caveat = Caveat({
  subsets: ["latin", "cyrillic"],
  weight: ["400", "500"],
});

export const SCRIPT_FONT = caveat.style.fontFamily;

/**
 * Serif for big stat numbers on the designer covers. Cormorant's digits are
 * old-style (5/4/8 dip below the baseline and vary in height), which reads
 * as misaligned at display sizes — Playfair uses even, lining figures, so
 * numbers always share one clean line regardless of the selected pairing.
 */
export const NUMBER_FONT = playfair.style.fontFamily;

export interface FontPairing {
  id: string;
  name: string;
  premium: boolean;
  display: string; // resolved font-family
  body: string;
}

export const FONT_PAIRINGS: FontPairing[] = [
  {
    id: "classic",
    name: "Classic Serif",
    premium: false,
    display: cormorant.style.fontFamily,
    body: montserrat.style.fontFamily,
  },
  {
    id: "editorial",
    name: "Editorial",
    premium: false,
    display: playfair.style.fontFamily,
    body: inter.style.fontFamily,
  },
  {
    id: "modern",
    name: "Modern",
    premium: true,
    display: unbounded.style.fontFamily,
    body: manrope.style.fontFamily,
  },
];

export const DEFAULT_PAIRING = FONT_PAIRINGS[0];

/**
 * `document.fonts.ready` only waits for fonts that have already started
 * loading — a font-face that's never been *used* yet (e.g. the very first
 * canvas draw with Kazakh/Cyrillic glyphs) can still fall back to a system
 * font for that first paint. Explicitly loading each family at the weights
 * templates draw with, using a sample containing every script, forces the
 * fetch to finish before any canvas is touched.
 */
export async function ensureFontsLoaded(pairing: FontPairing): Promise<void> {
  const sample = "Үлкентұмсықты шүрілдек торғай Обыкновенная чечевица Common Rosefinch 0123 @·№";
  const weights = ["300", "400", "500", "600"];
  await Promise.all(
    weights.flatMap((w) => [
      document.fonts.load(`${w} 100px ${pairing.display}`, sample),
      document.fonts.load(`${w} 100px ${pairing.body}`, sample),
    ]).concat([
      document.fonts.load(`italic 400 100px ${pairing.display}`, sample),
      document.fonts.load(`400 100px ${SCRIPT_FONT}`, sample),
      document.fonts.load(`500 100px ${SCRIPT_FONT}`, sample),
      document.fonts.load(`400 100px ${NUMBER_FONT}`, sample),
      document.fonts.load(`italic 400 100px ${NUMBER_FONT}`, sample),
    ])
  );
  await document.fonts.ready;
}
